import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {AnimationType, ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {AudioItemAudioPlayerObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {v4 as uuidv4} from 'uuid';
import type {ItemObject, SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';

export const getPagesArray = (pagesHashMap: Record<string, PageObject> | undefined, pageOrder: string[] | undefined): PageObject[] => {
  if (!pagesHashMap || !pageOrder) {
    return [];
  }
  return pageOrder.map((id) => pagesHashMap[id]).filter((page): page is PageObject => page !== undefined);
};

export const getPageStartAndEndTime = (pages: PageObject[], pageId: string): [number, number] | undefined => {
  let startTime = 0;

  for (const page of pages) {
    if (page.hashedID === pageId) {
      const endTime = startTime + (page.duration ?? 0);
      return [startTime, endTime];
    }
    startTime += page.duration ?? 0;
  }

  return undefined;
};

export const hasPageAnimation = (page: PageObject): boolean => {
  return Object.values(page.items.itemsHashMap).some((item) => item.introAnimation.type !== AnimationType.NONE || item.outroAnimation.type !== AnimationType.NONE);
};

export const hasItems = (page: PageObject): boolean => {
  return Object.keys(page.items.itemsHashMap).length > 0;
};

export const getAudioPlaybackDuration = (audioPlayer: AudioItemAudioPlayerObject): number => {
  const trimmedDuration = audioPlayer.trim.isTrimmed ? audioPlayer.trim.endTime - audioPlayer.trim.startTime : audioPlayer.originalDuration;
  const trimmedPlaybackDuration = trimmedDuration / audioPlayer.speed;
  return trimmedPlaybackDuration;
};

export const hasAudioClipsOnPage = (page: PageObject): boolean => {
  if (!page.audioClips) {
    return false;
  }

  return Object.keys(page.audioClips.audioItemsHashMap).length > 0;
};

export const isSlideshowStreaming = (slideshowItem: SlideshowItemObject): boolean => {
  const slides = slideshowItem.slides;

  if (!slides) {
    return false;
  }

  const hasVideoSlide = Object.values(slides.slidesHashMap).some((slide) => slide.gitype === ITEM_TYPE.VIDEOSLIDE);
  const isMultiSlide = Object.keys(slides.slidesHashMap).length > 1;
  const hasIntroDelay = slideshowItem.onPageStartTime > 0;
  const hasSingleSlide = Object.keys(slides.slidesHashMap).length === 1;
  const hasAnimation = slideshowItem.introAnimation?.type !== AnimationType.NONE || slideshowItem.outroAnimation?.type !== AnimationType.NONE;
  const hasTransition = slideshowItem.transition?.type !== AnimationType.NONE;

  return hasVideoSlide || isMultiSlide || hasIntroDelay || (hasSingleSlide && hasAnimation && hasTransition);
};

export const pageHasStreamingItem = (page: PageObject): boolean => {
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (
      item.gitype === ITEM_TYPE.VIDEO ||
      item.gitype === ITEM_TYPE.STICKER ||
      item.gitype === ITEM_TYPE.TRANSCRIPT ||
      (item.gitype === ITEM_TYPE.SLIDESHOW && isSlideshowStreaming(item as SlideshowItemObject))
    ) {
      return true;
    }
  }

  return false;
};

export const hasVideoBackground = (page: PageObject): boolean => {
  return page.background?.details?.type === BackgroundTypeName.VIDEO;
};

export const isPageVideo = (page: PageObject): boolean => {
  if (hasPageAnimation(page)) {
    return true;
  }

  if (hasAudioClipsOnPage(page)) {
    return true;
  }

  if (hasVideoBackground(page)) {
    return true;
  }

  return pageHasStreamingItem(page);
};

export const pageObjectWithNewUids = (pageObject: PageObject): PageObject => {
  const newPageId = uuidv4();

  let newBackground = pageObject.background;
  if (pageObject.background?.details?.type === BackgroundTypeName.IMAGE && pageObject.background.details.uid) {
    newBackground = {
      ...pageObject.background,
      details: {
        ...pageObject.background.details,
        uid: uuidv4(),
      },
    };
  }

  const newItemsHashMap: Record<string, ItemObject> = {};
  const itemsUidMap = new Map<string, string>();
  const itemsOrderLength = pageObject.items.itemsOrder?.length ?? 0;
  const newItemsOrder: string[] = new Array(itemsOrderLength);

  for (const [oldItemUid, itemObject] of Object.entries(pageObject.items.itemsHashMap)) {
    const newItemUid = uuidv4();
    itemsUidMap.set(oldItemUid, newItemUid);

    let newItemObject: ItemObject = {
      ...itemObject,
      uid: newItemUid,
    };

    if (itemObject.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = itemObject as SlideshowItemObject;
      if (slideshowItem.slides?.slidesHashMap) {
        const newSlidesHashMap: Record<string, SlideItemObject> = {};
        const slidesOrderLength = slideshowItem.slides.slidesOrder?.length ?? 0;
        const newSlidesOrder: string[] = new Array(slidesOrderLength);
        const slidesUidMap = new Map<string, string>();

        for (const [oldSlideUid, slideObject] of Object.entries(slideshowItem.slides.slidesHashMap)) {
          const newSlideUid = uuidv4();
          slidesUidMap.set(oldSlideUid, newSlideUid);
          newSlidesHashMap[newSlideUid] = {
            ...slideObject,
            uid: newSlideUid,
          };
        }

        for (let i = 0; i < slidesOrderLength; i++) {
          const oldSlideUid = slideshowItem.slides.slidesOrder[i];
          if (oldSlideUid) {
            const newSlideUid = slidesUidMap.get(oldSlideUid);
            if (newSlideUid !== undefined) {
              newSlidesOrder[i] = newSlideUid;
            }
          }
        }

        newItemObject = {
          ...newItemObject,
          slides: {
            ...slideshowItem.slides,
            slidesHashMap: newSlidesHashMap,
            slidesOrder: newSlidesOrder.filter(Boolean),
          },
          selectedSlideUID: '',
        } as ItemObject;
      }
    }

    if (itemObject.gitype === ITEM_TYPE.TRANSCRIPT) {
      const transcriptItem = itemObject as TranscriptItemObject;
      if (transcriptItem.subtitlesHashmap) {
        const newSubtitlesHashmap: Record<string, SubtitleObject> = {};
        for (const [, subtitleObject] of Object.entries(transcriptItem.subtitlesHashmap)) {
          const newSubtitleUid = uuidv4();
          newSubtitlesHashmap[newSubtitleUid] = {
            ...subtitleObject,
            subtitleUID: newSubtitleUid,
          };
        }
        newItemObject = {
          ...newItemObject,
          subtitlesHashmap: newSubtitlesHashmap,
        } as ItemObject;
      }
    }

    newItemsHashMap[newItemUid] = newItemObject;
  }

  for (let i = 0; i < itemsOrderLength; i++) {
    const oldItemUid = pageObject.items.itemsOrder[i];
    if (oldItemUid) {
      const newItemUid = itemsUidMap.get(oldItemUid);
      if (newItemUid !== undefined) {
        newItemsOrder[i] = newItemUid;
      }
    }
  }

  return {
    ...pageObject,
    hashedID: newPageId,
    background: newBackground,
    items: {
      itemsHashMap: newItemsHashMap,
      itemsOrder: newItemsOrder.filter(Boolean),
      groups: pageObject.items.groups,
    },
  };
};
